import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { getActiveCollectionId } from './collectionStore';

interface StockRequestSettings {
  id: string | null;
  collectionId: string;
  defaultApproverIds: string[];
  defaultOrdererIds: string[];
}

interface StockRequestSettingsState {
  settings: StockRequestSettings | null;
  isLoading: boolean;
  error: string | null;

  fetchSettings: () => Promise<void>;
  saveSettings: (defaultApproverIds: string[], defaultOrdererIds: string[]) => Promise<boolean>;
}

export const useStockRequestSettingsStore = create<StockRequestSettingsState>((set, get) => ({
  settings: null,
  isLoading: false,
  error: null,

  fetchSettings: async () => {
    const collectionId = getActiveCollectionId();
    if (!collectionId) { set({ settings: null }); return; }

    set({ isLoading: true, error: null });
    const { data, error } = await supabase
      .from('stock_request_settings')
      .select('*')
      .eq('collection_id', collectionId)
      .maybeSingle();

    if (error) { set({ isLoading: false, error: error.message }); return; }

    set({
      isLoading: false,
      settings: data
        ? {
            id: data.id,
            collectionId: data.collection_id,
            defaultApproverIds: data.default_approver_ids ?? [],
            defaultOrdererIds: data.default_orderer_ids ?? [],
          }
        : { id: null, collectionId, defaultApproverIds: [], defaultOrdererIds: [] },
    });
  },

  saveSettings: async (defaultApproverIds: string[], defaultOrdererIds: string[]) => {
    const collectionId = getActiveCollectionId();
    if (!collectionId) return false;

    set({ error: null });
    const existing = get().settings;

    if (existing?.id) {
      const { error } = await supabase
        .from('stock_request_settings')
        .update({ default_approver_ids: defaultApproverIds, default_orderer_ids: defaultOrdererIds })
        .eq('id', existing.id);
      if (error) { set({ error: error.message }); return false; }
    } else {
      const { data, error } = await supabase
        .from('stock_request_settings')
        .insert({
          collection_id: collectionId,
          default_approver_ids: defaultApproverIds,
          default_orderer_ids: defaultOrdererIds,
        })
        .select()
        .single();
      if (error) { set({ error: error.message }); return false; }
      set({
        settings: {
          id: data.id,
          collectionId,
          defaultApproverIds,
          defaultOrdererIds,
        },
      });
      return true;
    }

    set(s => ({
      settings: s.settings
        ? { ...s.settings, defaultApproverIds, defaultOrdererIds }
        : null,
    }));
    return true;
  },
}));
